import { ChangeEvent } from "react";
import { RequiredManager } from "../../../shared/RequieredManager";
import { CriterionModel, Operator } from "../../providers/CriteriaContext";
import { AdditionalDefinition } from "../Criterion";
import Dropdown from "../../../shared/components/Dropdown";
import { InputWithValidation } from "./InputWithValidation";
import { ListSelector } from "./ListSelector";

type ClientInvoiceCriterionProps = {
  data: CriterionModel;
  definitions: { text: string; value: AdditionalDefinition }[];
  definition: AdditionalDefinition | undefined;
  textOperators: any[];
  listOperators: any[];
  ioListOperators: any[];
  dateOperators: any[];
  dateOperatorsWithEmpty: any[];
  inputResetKey: number;
  listValue: { value: string; label: string }[];
  onDefinitionChange: (definition: AdditionalDefinition) => void;
  onOperatorChange: (value: number) => void;
  onValueChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onListChange: (
    value: string[] | undefined,
    items: { value: string; label: string }[]
  ) => void;
  requiredManager: RequiredManager;
  translate: any;
};

const EXCEPTION_ADDITIONAL_LIST = [-9];

export const ClientInvoiceCriterion = ({
  data,
  definitions,
  definition,
  textOperators,
  listOperators,
  ioListOperators,
  dateOperators,
  dateOperatorsWithEmpty,
  inputResetKey,
  listValue,
  onDefinitionChange,
  onOperatorChange,
  onValueChange,
  onListChange,
  requiredManager,
  translate
}: ClientInvoiceCriterionProps) => {
  const hasListWithAdditional = EXCEPTION_ADDITIONAL_LIST.includes(
    definition?.AdditionalDefinitionID ?? 0
  );

  const renderTextType = () => (
    <>
      <Dropdown
        selectClasses=""
        onChange={onOperatorChange}
        items={textOperators}
        value={data.Operator}
      />
      {![Operator.Empty, Operator.NotEmpty].includes(data.Operator) && (
        <InputWithValidation
          inputResetKey={inputResetKey}
          type="text"
          item={data}
          filterKey="Value"
          callback={onValueChange}
          requiredManager={requiredManager}
          defaultValue={data}
        />
      )}
    </>
  );

  const renderListType = () => (
    <>
      <Dropdown
        selectClasses=""
        onChange={onOperatorChange}
        items={[...textOperators.slice(0, 2), ...listOperators]}
        value={data.Operator}
      />
      {[Operator.Contains, Operator.DoesntContain].includes(data.Operator) && (
        <InputWithValidation
          inputResetKey={inputResetKey}
          type="text"
          item={data}
          filterKey="Value"
          callback={onValueChange}
          requiredManager={requiredManager}
        />
      )}
      {![
        Operator.Contains,
        Operator.DoesntContain,
        Operator.Empty,
        Operator.NotEmpty,
        Operator.Dynamic
      ].includes(data.Operator) &&
        definition && (
          <ListSelector
            listValue={listValue}
            items={definition.AdditionalDefinitionItems}
            onListChange={onListChange}
            requiredManager={requiredManager}
            placeholder={translate.Select}
          />
        )}
    </>
  );

  const renderDateType = () => {
    return (
      <>
        <Dropdown
          selectClasses=""
          onChange={onOperatorChange}
          items={dateOperatorsWithEmpty}
          value={data.Operator}
        />
        {![Operator.Empty, Operator.NotEmpty].includes(data.Operator) && (
          <InputWithValidation
            inputResetKey={inputResetKey}
            type="number"
            item={data}
            filterKey="Value"
            callback={onValueChange}
            requiredManager={requiredManager}
            boxLeft={false}
            withBox={true}
            boxText="dias"
          />
        )}
      </>
    );
  };

  const renderAmountType = () => (
    <>
      <Dropdown
        selectClasses=""
        onChange={onOperatorChange}
        items={dateOperatorsWithEmpty}
        value={data.Operator}
      />
      {![Operator.Empty, Operator.NotEmpty].includes(data.Operator) && (
        <InputWithValidation
          inputResetKey={inputResetKey}
          type="number"
          item={data}
          filterKey="Value"
          callback={onValueChange}
          requiredManager={requiredManager}
          boxLeft={true}
          withBox={false}
        />
      )}
    </>
  );

  const renderGenericListType = () => (
    <>
      <Dropdown
        selectClasses=""
        onChange={onOperatorChange}
        items={dateOperators}
        value={data.Operator}
      />
      {![Operator.Empty, Operator.NotEmpty].includes(data.Operator) && (
        <InputWithValidation
          inputResetKey={inputResetKey}
          type="text"
          item={data}
          filterKey="Value"
          callback={onValueChange}
          requiredManager={requiredManager}
        />
      )}
    </>
  );

  const renderExceptionList = () => (
    <>
      <Dropdown
        selectClasses=""
        onChange={onOperatorChange}
        items={ioListOperators}
        value={data.Operator}
      />
      {definition && (
        <ListSelector
          listValue={listValue}
          items={definition.AdditionalDefinitionItems}
          onListChange={onListChange}
          requiredManager={requiredManager}
          placeholder={translate.Select}
        />
      )}
    </>
  );

  const renderByDefinitionType = () => {
    if (!definition) return null;

    // Text type (0 or 5) - 6 (link)
    if (
      definition.type === 0 ||
      definition.type === 5 ||
      definition.type === 6
    ) {
      return renderTextType();
    }

    // List type (1) - not in exception list
    if (
      definition.type === 1 &&
      ![-9].includes(definition.AdditionalDefinitionID)
    ) {
      return renderListType();
    }

    // Date type (2)
    if (definition.type === 2) {
      return renderDateType();
    }

    // Amount/numeric type (3)
    if (definition.type === 3) {
      return renderAmountType();
    }

    // Generic list type (4)
    if (definition.type === 4) {
      return renderGenericListType();
    }

    return null;
  };

  return (
    <>
      <Dropdown
        selectClasses=""
        onChange={onDefinitionChange}
        items={definitions}
        value={definition}
      />
      {renderByDefinitionType()}
      {hasListWithAdditional && renderExceptionList()}
    </>
  );
};
